/**
 * Zeitpläne der Cron-Jobs — prüfen und in Worte fassen (Server und UI).
 *
 * Cloud Scheduler versteht zwei Schreibweisen: unix-cron (`0 1 * * *`) und die
 * App-Engine-Form (`every 10 minutes`). Die Prüfung hier ist bewusst grob —
 * sie fängt Tippfehler ab, bevor ein Request nach Google geht; die letzte
 * Instanz bleibt Google selbst (400 mit Grund, der bis in die Oberfläche
 * durchgereicht wird).
 */

export const SCHEDULE_MAX_LENGTH = 100;

/** Zeitzonen, die die Oberfläche anbietet — IANA-Namen, wie Cloud Scheduler sie verlangt. */
export const SCHEDULE_TIME_ZONES = ['Europe/Berlin', 'UTC'] as const;
export type ScheduleTimeZone = (typeof SCHEDULE_TIME_ZONES)[number];

export class CronScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CronScheduleError';
  }
}

/** Zeichen, die in einem unix-cron-Feld vorkommen dürfen (Zahlen, Namen, * / , - ? L #). */
const CRON_FIELD = /^[0-9A-Za-z*/,?#L\-]+$/;

/** Trimmt, glättet Whitespace und prüft die Grundform. Wirft CronScheduleError. */
export function normalizeSchedule(input: unknown): string {
  if (typeof input !== 'string' || input.trim() === '') {
    throw new CronScheduleError('Zeitplan fehlt.');
  }
  const schedule = input.trim().replace(/\s+/g, ' ');
  if (schedule.length > SCHEDULE_MAX_LENGTH) {
    throw new CronScheduleError(`Der Zeitplan ist länger als ${SCHEDULE_MAX_LENGTH} Zeichen.`);
  }
  if (/^every /i.test(schedule)) return schedule;
  const fields = schedule.split(' ');
  if (fields.length !== 5) {
    throw new CronScheduleError(
      'Ein Cron-Zeitplan hat fünf Felder: Minute Stunde Tag Monat Wochentag — zum Beispiel „0 1 * * *".',
    );
  }
  const bad = fields.find((f) => !CRON_FIELD.test(f));
  if (bad !== undefined) throw new CronScheduleError(`Ungültiges Feld „${bad}" im Zeitplan.`);
  return schedule;
}

export function normalizeTimeZone(input: unknown): ScheduleTimeZone {
  const value = typeof input === 'string' ? input.trim().toLowerCase() : '';
  const found = SCHEDULE_TIME_ZONES.find((z) => z.toLowerCase() === value);
  if (!found) throw new CronScheduleError(`Die Zeitzone muss eine von ${SCHEDULE_TIME_ZONES.join(', ')} sein.`);
  return found;
}

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function twoDigits(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Menschlich lesbare Form der häufigen Zeitpläne. Alles andere kommt
 * unverändert zurück — lieber der rohe Ausdruck als eine falsche Übersetzung.
 */
export function describeSchedule(schedule: string): string {
  const s = schedule.trim().replace(/\s+/g, ' ');
  if (/^every /i.test(s)) return s;
  const parts = s.split(' ');
  if (parts.length !== 5) return s;
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  if (dayOfMonth !== '*' || month !== '*') return s;

  const every = /^\*\/(\d+)$/;
  const everyMinutes = minute.match(every);
  if (everyMinutes && hour === '*' && dayOfWeek === '*') return `alle ${everyMinutes[1]} Minuten`;

  const everyHours = hour.match(every);
  if (everyHours && /^\d+$/.test(minute) && dayOfWeek === '*') {
    return Number(minute) === 0 ? `alle ${everyHours[1]} Stunden` : `alle ${everyHours[1]} Stunden (Minute ${minute})`;
  }
  if (/^\d+$/.test(minute) && hour === '*' && dayOfWeek === '*') {
    return Number(minute) === 0 ? 'stündlich' : `stündlich zur Minute ${minute}`;
  }
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) return s;

  const time = `${twoDigits(Number(hour))}:${twoDigits(Number(minute))}`;
  if (dayOfWeek === '*') return `täglich um ${time}`;
  if (dayOfWeek === '1-5') return `werktags um ${time}`;
  if (dayOfWeek === '0,6' || dayOfWeek === '6,0' || dayOfWeek === '6-7') return `am Wochenende um ${time}`;
  if (/^[0-7]$/.test(dayOfWeek)) return `jeden ${WEEKDAYS[Number(dayOfWeek)]} um ${time}`;
  return s;
}
